<div class="scroller main-container">
    <section class="faq">
        <h1 class="mat-headline-1">Frequently Asked Questions</h1>
        <mat-accordion>
            <mat-expansion-panel class="mat-elevation-z0">
                <mat-expansion-panel-header [collapsedHeight]="'4rem'">
                    <mat-panel-title> What is the "Intervention Tracker"?</mat-panel-title>
                </mat-expansion-panel-header>
                <p>
                    The intervention tracker is a tool to efficiently report frequency and downtime caused by
                    re-occurring deviations.
                    The main objective is to quantify the frequency and amount of time we spend on specific deviations,
                    so we will be able to prioritize our efforts better.
                    Other objectives of the tracker are:
                </p>
                <ul>
                    <li>Replace the Excel sheets, forms and other methods for reporting interventions with one tool that
                        will enable quick, easy and higher quality reporting.</li>
                    <li>Shortening the distance between our current ticketing system used in technology "Azure DevOps"
                        (ADO), and our deployment engineers.
                    </li>
                </ul>
            </mat-expansion-panel>
        </mat-accordion>
        <mat-divider></mat-divider>
        <mat-accordion>
            <mat-expansion-panel class="mat-elevation-z0">
                <mat-expansion-panel-header [collapsedHeight]="'4rem'">
                    <mat-panel-title> Why do we need "Intervention Tracker"?</mat-panel-title>
                </mat-expansion-panel-header>
                <p>
                    The main value of reporting interventions for known deviations, is that it will enable us to
                    prioritize deviations more efficiently.
                    The "Intervention Tracker" app provides a scalable and centralized solution for reporting
                    interventions.
                    It is efficient to use and will enable the user to report in a meaningful way, linking reports to
                    specific deviations.
                </p>
            </mat-expansion-panel>
        </mat-accordion>
        <mat-divider></mat-divider>
        <mat-accordion>
            <mat-expansion-panel class="mat-elevation-z0">
                <mat-expansion-panel-header [collapsedHeight]="'4rem'">
                    <mat-panel-title> Who can use the "Intervention Tracker"?</mat-panel-title>
                </mat-expansion-panel-header>
                <p>
                    The intervention tracker is intended for internal use in Wheel.me only, and should not be used
                    outside the organization.
                </p>
            </mat-expansion-panel>
        </mat-accordion>
        <mat-divider></mat-divider>
        <mat-accordion>
            <mat-expansion-panel class="mat-elevation-z0">
                <mat-expansion-panel-header [collapsedHeight]="'4rem'">
                    <mat-panel-title>What is the difference between an "intervention" and a
                        "deviation"?</mat-panel-title>
                </mat-expansion-panel-header>
                <p>
                    An intervention is:
                    "<span id="definition">Any human interaction done to make the robot operate as intended</span>".
                    Examples of interventions for Genius 2:
                </p>
                <ul>
                    <li>Localizing the robot by driving it manually</li>
                    <li>Replacing batteries</li>
                    <li>Replacing mecanum wheels</li>
                </ul>
                <p>
                    A deviation is:
                    "<span id="definition">Failure to fulfill a requirement</span>".
                    Examples of deviations for Genius 2:
                </p>
                <ul>
                    <li>Mecanum wheel does not rotate</li>
                    <li>Robot delocalizes</li>
                    <li>Robot does not charge</li>
                </ul>
                <p>
                    An intervention is therefore the <span id="definition">action</span> done to resolve a particular
                    <span id="definition">deviations</span>.
                </p>
            </mat-expansion-panel>
        </mat-accordion>
        <mat-divider></mat-divider>
        <mat-accordion>
            <mat-expansion-panel id="faq-section-id" class="mat-elevation-z0">
                <mat-expansion-panel-header [collapsedHeight]="'4rem'">
                    <mat-panel-title>When should I report a deviation and when should I report an
                        intervention?</mat-panel-title>
                </mat-expansion-panel-header>
                <p>

                    You should report a <span id="definition">deviation</span> when you can't find a deviation that is
                    similar in the list of existing deviations.
                    Reporting deviations should be done by sending a mail to "support&#64;wheel.me".
                    This can also be done through the app if the customers use cloud environments.
                </p>
                <br />
                <p>
                    You should report an <span id="definition">intervention</span> whenever you do an action to make the
                    robot operate as expected.
                    In general, you should "always" report interventions to known deviations.
                    However, in some cases you might not find a matching deviation to report the intervention to.
                    In these cases you can report an intervention with no linked deviation in these cases.
                </p>
                <ul>
                    <li>It is a reoccurring issue on the site, and you have already reported the deviation</li>
                    <li>You don't have enough information about to report the deviation properly. Then you can report it
                        as an intervention to keep track of all actions you have done throughout the debugging
                        process,
                        and use this to report a deviation later.</li>
                </ul>
                <br />
                <p>
                    The process flow will when you have an issue on site will therefore be:
                </p>
                <ol>
                    <li>A deviation appears on site</li>
                    <li>Check the list of deivations in the "Report Intervention" page</li>
                    <ol type="a">
                        <li>If you find your deviation: Report the intervention</li>
                        <li>If you find you can't find your deviation, and you have enough details to report it
                            properly: Report it by sending a mail to support&#64;wheel.me</li>
                        <li>If you find you can't find your deviation, and you have too little details to report it
                            properly: Report the intervention without a link to a deviation, and report the deviation
                            once you have enough information.</li>
                    </ol>
                </ol>
            </mat-expansion-panel>
        </mat-accordion>
        <mat-divider></mat-divider>
        @if(this.roleService.showAnalytics(this.roleService.getRole())) {
        <mat-accordion class="analytics">
            <mat-expansion-panel class="mat-elevation-z0">
                <mat-expansion-panel-header [collapsedHeight]="'4rem'">
                    <mat-panel-title>Where can I see analytics from reported interventions?</mat-panel-title>
                </mat-expansion-panel-header>
                <p>
                    Simple analytics are available in the "Intervention Analytics" page.
                    If you need to get the reported interventions exported as a CSV file for more detailed analysis,
                    please contact jorgen.haaland&#64;wheel.me.
                </p>
            </mat-expansion-panel>
        </mat-accordion>
        }
    </section>

    <div class="howto-container">
        <section class="howto">
            <mat-divider style="border-top-color: var(--sys-secondary); border-top-width: 2px"></mat-divider>
            <h2>
                Reporting Interventions
            </h2>
            <div class="instruction-container">
                <div class="text">
                    <h3>
                        Step 1: Open the Intervention Tracker
                    </h3>
                    <p>
                        Press the menu at the top left.
                        In the dropdown, choose "Intervention Tracker"
                    </p>
                </div>
                <img src="assets/images/step_1.png" alt="Step 1">
            </div>
            <mat-divider />
            <div class="instruction-container">
                <div class="text">
                    <h3>
                        Step 2: Search for a deviation that matches your issue
                    </h3>
                    <p>
                        Next step is to try to find a deviation that has a description that matches your issue.
                        The recommended approach is to search for a word in the title/description.
                        If you want, you can also filter on various fields from ADO.
                        These are:
                    </p>
                    <ul>
                        <li> HW Component </li>
                        <li> Issue Group </li>
                    </ul>
                    <p>
                        In most cases you will experience repeated deviations.
                        To sort the list of deviations based on number of reports, press the table header "Number of
                        Reports"
                        (this is also done by default).
                        The most frequent deviations will then appear on the top of the list.
                    </p>
                </div>
                <img src="assets/images/step_2.png" alt="Step 2">
            </div>
            <mat-divider />
            <div class="instruction-container">
                <div class="text">
                    <h3>
                        Step 3: Expand the deviation and press "Report Intervention"
                    </h3>
                    <ol>
                        <li>
                            <div class="center-vertically">
                                Press the deviation you want to expand
                            </div>
                        </li>
                        <li>Press the button "Report Intervention"</li>
                    </ol>
                </div>
                <img src="assets/images/step_3.png" alt="Step 3">
            </div>
            <mat-divider />
            <div class="instruction-container">
                <div class="text">
                    <h3>
                        Step 4: Fill in required fields in the dialog and report intervention
                    </h3>
                    <p>
                        Fill in the required fields in the dialog.
                        The required fields when you report an intervention linked to existing deviation are:
                    </p>
                    <ul>
                        <li> HW version </li>
                        <li> SW version </li>
                        <li> Customer </li>
                        <li> Usecase </li>
                        <li> Minutes of downtime </li>
                        <li> Actions done to resolve the issue: You can add as many actions as you want, but minimum one
                            is required.
                            If you can't find your action in the dropdown for "Common actions" you can specify it as a
                            "Custom action".
                        </li>
                    </ul>
                    <p>
                        The "Description"- and "Cart" fields is optional, and you can use it to give more context to
                        your deviation if required.
                    </p>
                    <p>
                        If you report without a linked deviation, there are two additional fields required:
                    </p>
                    <ul>
                        <li>Description</li>
                        <li>Issue-group</li>
                    </ul>

                    <p>
                        To make the reporting more efficient, all versions are stored for the next time an
                        intervention
                        is reported.
                    </p>
                    <p>
                        When the deviation is reported, you will get a notification in the bottom of the page if the
                        report was successfully reported or not:
                    </p>
                    <img src="assets/images/step_5.png" alt="Step 5">
                </div>
                <img src="assets/images/step_4.png" alt="Step 4">
            </div>
            <mat-divider />
            <div class="instruction-container">
                <div class="text">
                    <h3>
                        What if I can't find the related deviation?
                    </h3>
                    <p>
                        In some cases, you will not find your deviation in the list.
                        In this case, you can report an intervention without any related deviation.
                        Please see FAQ section above on
                        <a style="color: blue" (click)="scrollToFaqElement('faq-section-id')">"When should I report a
                            deviation and when should I report an
                            intervention?"</a>
                        for guidelines for this.
                    </p>
                    <ol>
                        <li>Scroll to the bottom of the site</li>
                        <li>Press "Report Intervention"</li>
                        <li>Fill in the dialog</li>
                    </ol>
                    <p>
                        The dialog for reporting will have a new field called "Issue Group".
                        This contains a list of common issue groups.
                        It is highly suggested to fill in this, even though it is optional.
                        If you can't find your issue group in the suggestions, you can write any other custom issue
                        groups in the input field.
                    </p>
                </div>
                <img src="assets/images/step_6.png" alt="Intervention Tracker">
            </div>
        </section>
    </div>
    <section style="text-align: center">
        <mat-divider style="border-top-color: var(--sys-secondary); border-top-width: 2px"></mat-divider>
        <h2>Share your feedback</h2>
        <p>
            If you have any issues, feature requests or any other feedback, please email us
        </p>
    </section>
    <button mat-fab extended aria-label="Send Feedback" (click)="openMailClient()">
        <mat-icon>mail</mat-icon>
        Give Feedback
    </button>
</div>
